/*
 * FAQ: turn the rendered groups into a tab set, and honour deep links.
 *
 * Everything here is an enhancement. Liquid renders every group one after
 * another behind its own heading, and the tab strip as plain anchors pointing
 * at those headings - so with no JavaScript the page is complete and every
 * answer still opens. This file only takes the navigation over.
 */
(function () {
  'use strict';

  var ALL = 'all';

  var KEYS = {
    left: 37,
    up: 38,
    right: 39,
    down: 40,
    home: 36,
    end: 35
  };

  function Faq(root) {
    this.root = root;
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-faq-tab]'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-faq-panel]'));
    this.track = root.querySelector('[data-faq-tabs-track]');

    // One group and no strip is just a list. Leave it exactly as rendered.
    if (this.tabs.length < 2 || !this.panels.length) return;

    this.upgrade();
    this.bind();

    /*
     * A hash may name a question rather than a group, so it decides the opening
     * tab. Only fall back to the first tab when it does not resolve.
     */
    if (!this.openFromHash(false)) this.select(this.tabs[0], false);

    root.classList.add('is-tabbed');
  }

  /* Swap the anchor semantics for tab semantics, now that they will work. */
  Faq.prototype.upgrade = function () {
    var list = this.track || this.tabs[0].parentNode;
    list.setAttribute('role', 'tablist');

    /*
     * The tablist has to sit on the tabs' own parent, so the label moves there
     * with it and the outer <nav> steps aside - a landmark that has lost its
     * name, wrapped around a control that now carries the semantics, is just
     * one more thing to tab past. Without the script it stays a real nav.
     */
    var nav = this.root.querySelector('[data-faq-tabs]');
    if (nav && nav !== list) {
      if (nav.hasAttribute('aria-label')) {
        list.setAttribute('aria-label', nav.getAttribute('aria-label'));
        nav.removeAttribute('aria-label');
      }
      nav.setAttribute('role', 'presentation');
    }

    /*
     * The everything tab governs every panel at once, and aria-controls takes
     * a list, so say so rather than picking one panel and lying about the rest.
     */
    var everyPanel = this.panels.map(function (panel) {
      return panel.id;
    }).join(' ');

    this.tabs.forEach(function (tab) {
      tab.setAttribute('role', 'tab');
      tab.setAttribute(
        'aria-controls',
        tab.getAttribute('data-faq-tab') === ALL ? everyPanel : tab.getAttribute('href').slice(1)
      );
    });

    this.panels.forEach(function (panel) {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('tabindex', '-1');
    });
  };

  Faq.prototype.bind = function () {
    var self = this;

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        self.select(tab, true);
      });

      tab.addEventListener('keydown', function (e) {
        var i = self.tabs.indexOf(tab);
        var next = -1;

        if (e.keyCode === KEYS.left || e.keyCode === KEYS.up) next = i - 1;
        else if (e.keyCode === KEYS.right || e.keyCode === KEYS.down) next = i + 1;
        else if (e.keyCode === KEYS.home) next = 0;
        else if (e.keyCode === KEYS.end) next = self.tabs.length - 1;
        else return;

        e.preventDefault();
        if (next < 0) next = self.tabs.length - 1;
        if (next >= self.tabs.length) next = 0;

        var target = self.tabs[next];
        self.select(target, true);
        target.focus();
      });
    });

    window.addEventListener('hashchange', function () {
      self.openFromHash(true);
    });
  };

  /*
   * `move` distinguishes a click from first paint: selecting a tab on load must
   * not scroll the page, or arriving at the FAQ would jump past the heading.
   */
  Faq.prototype.select = function (tab, move) {
    var slug = tab.getAttribute('data-faq-tab');
    var all = slug === ALL;

    this.tabs.forEach(function (t) {
      var on = t === tab;
      t.setAttribute('aria-selected', String(on));
      // Roving tabindex: one stop for the whole strip, arrows move within it.
      t.setAttribute('tabindex', on ? '0' : '-1');
      t.classList.toggle('is-active', on);
    });

    this.panels.forEach(function (panel) {
      panel.hidden = !all && panel.getAttribute('data-faq-panel') !== slug;
    });

    /*
     * With every group on screen at once the group headings have to come back:
     * they are the only thing separating one list from the next, and the tab
     * above no longer names what you are looking at.
     */
    this.root.classList.toggle('is-all', all);

    if (move) this.scrollTabIntoView(tab);
    this.active = tab;
  };

  /* Keep the chosen tab visible when the strip scrolls sideways on mobile. */
  Faq.prototype.scrollTabIntoView = function (tab) {
    if (!this.track || !this.track.scrollWidth) return;
    if (this.track.scrollWidth <= this.track.clientWidth) return;

    var left = tab.offsetLeft - (this.track.clientWidth - tab.offsetWidth) / 2;
    if (this.track.scrollTo) this.track.scrollTo({ left: left, behavior: 'smooth' });
    else this.track.scrollLeft = left;
  };

  /*
   * A link to #a-question should select its tab, open it and go there - not
   * land on a tab that is hidden. Returns whether the hash resolved.
   */
  Faq.prototype.openFromHash = function (move) {
    var id = (window.location.hash || '').replace('#', '');
    if (!id) return false;

    var target;
    try {
      target = this.root.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(id) : id));
    } catch (e) {
      return false;
    }
    if (!target) return false;

    // The hash may name the group itself, or a question inside it.
    var panel = target.hasAttribute('data-faq-panel')
      ? target
      : target.closest
        ? target.closest('[data-faq-panel]')
        : null;

    // ...or the list as a whole, which is the everything tab's own anchor.
    var slug = panel ? panel.getAttribute('data-faq-panel') : ALL;
    if (!panel && !target.querySelector('[data-faq-panel]')) return false;

    var tab = this.tabFor(slug);
    if (!tab) return false;

    this.select(tab, move);

    var item = target.hasAttribute('data-faq-item')
      ? target
      : target.closest
        ? target.closest('[data-faq-item]')
        : null;
    if (item) item.open = true;

    if (move) (item || panel || target).scrollIntoView({ block: 'start', behavior: 'smooth' });
    return true;
  };

  Faq.prototype.tabFor = function (slug) {
    for (var i = 0; i < this.tabs.length; i++) {
      if (this.tabs[i].getAttribute('data-faq-tab') === slug) return this.tabs[i];
    }
    return null;
  };

  function init() {
    var roots = document.querySelectorAll('[data-faq]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root._faq) return;
      root._faq = new Faq(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor replaces a section's markup wholesale on every edit.
  document.addEventListener('shopify:section:load', init);
})();
